import * as actions from './actions.js';
import { ReduxUtils } from '@dw/pwa-helpers/redux-utils.js';
import { buildContextKey, stripChipsByIds } from './utils.js';

const INITIAL_STATE = {
  // Panel dimensions - PERSISTED via persistEnhancer
  panelWidths: {
    board: 450,   // AI chat panel width when opened from board context
    card: 450,    // AI chat panel width when opened from card context
    home: 450,    // AI chat panel width when opened from home/explorer context
    onboarding: 450, // AI chat panel width when opened from onboarding/signup context
    // cardContent is NOT in initial state — it is only written when the user explicitly
    // resizes the card. The selector migrates from the legacy 'cardDetail' key on first use.
  },

  // FAB positions - PERSISTED via persistEnhancer
  fabPositions: {
    board: { x: 20, y: 20 }, // FAB position for board context
    card: { x: 60, y: 20 }, // FAB position for card context (top-anchored: y is from top)
    home: { x: 20, y: 20 }, // FAB position for home/explorer context
    onboarding: { x: 20, y: 20 }, // FAB position for onboarding/signup context
  },

  // Draft messages for existing chats - PERSISTED via persistEnhancer
  // Structure: { [chatId]: draftText }
  draftMessages: {},

  // Draft messages for new chats (before chatId exists) - PERSISTED via persistEnhancer
  // Structure: { [contextKey]: draftText }
  // Context key format: 'board_${boardId}' or 'card_${boardId}_${cardId}'
  newChatDrafts: {},

  // Pre-generated chatIds for new chats with file attachments - PERSISTED via persistEnhancer
  // Ensures the same chatId is used for both upload and startNewChat, even after page refresh
  // Structure: { [contextKey]: chatId }
  newChatIds: {},

  // Editing messages - PERSISTED via persistEnhancer
  // Structure: { [chatId]: messageId }
  // Tracks which message is being edited in each chat
  editMessages: {},

  // In-progress edit text for the message being edited - PERSISTED via persistEnhancer
  // Structure: { [chatId]: draftText }
  // Kept separate from `draftMessages` (the unsent draft); cleared when edit mode ends.
  editDrafts: {},

  // Board creations that have been auto-navigated - PERSISTED via persistEnhancer
  // Structure: { [`${chatId}_${toolMessageId}`]: true }
  // Prevents re-navigation when opening the same chat later
  completedBoardCreations: {},


  // Messages that have a pending cancel request - NOT persisted (temporary UI state)
  // Structure: { [messageId]: true }
  // Prevents cancel button from reappearing when Firestore snapshots temporarily reset status to GENERATING
  cancelMessages: {},

  // Tool calls the user has approved or rejected - NOT persisted (temporary UI state)
  // Structure: { [toolCallId]: 'APPROVED' | 'REJECTED' }
  // Approving localWrites the new status for instant feedback, but a Firestore snapshot
  // temporarily resets it to WAITING_FOR_APPROVAL until the server's own status lands —
  // long enough to flash the proposal back onto the card. Selectors treat these tool
  // calls as no longer awaiting approval. Cleared by undo-reject, which deliberately
  // returns a proposal to WAITING_FOR_APPROVAL.
  resolvedToolCalls: {},

  // Auto-submit pending state - NOT persisted (temporary UI state)
  // Structure: { [contextKey]: true }
  // Set when user clicks Send while uploads are in progress; cleared on completion, failure, or cancel
  autoSubmitPending: {},

  // Resize state - NOT persisted (temporary UI state)
  isResizing: false,

  // Chats that failed to load due to permission denied - NOT persisted (temporary UI state)
  // Structure: { [chatId]: true }
  accessDeniedChats: {},

  // Cross-panel hover sync — NOT persisted. Set when the user hovers a card
  // title in any AI Helper tool-call entry; the matching <kerika-board-card>
  // reads this and reflects an `[ai-hovered]` host attribute. `null` when no
  // panel row is being hovered. Cleared defensively by the panel's
  // `disconnectedCallback` to avoid a stale value if mouseleave is missed.
  hoverCardId: null,
};

export const aiChatReducer = (state = INITIAL_STATE, action) => {
  let newState = {...state};
  switch (action.type) {
    case actions.UPDATE_AI_CHAT_WIDTH: {
      let contextKey;
      if (action.context === 'BOARD') contextKey = 'board';
      else if (action.context === 'HOME') contextKey = 'home';
      else if (action.context === 'ONBOARDING') contextKey = 'onboarding';
      else contextKey = 'card';
      return ReduxUtils.replace(state, `panelWidths.${contextKey}`, action.width);
    }

    case actions.UPDATE_CARD_DETAIL_WIDTH:
      return ReduxUtils.replace(state, 'panelWidths.cardContent', action.width);

    case actions.UPDATE_FAB_POSITION: {
      let positionKey;
      switch (action.context) {
        case 'BOARD': positionKey = 'board'; break;
        case 'CARD': positionKey = 'card'; break;
        case 'HOME': positionKey = 'home'; break;
        case 'ONBOARDING': positionKey = 'onboarding'; break;
        default: positionKey = 'board';
      }
      return ReduxUtils.replace(state, `fabPositions.${positionKey}`, action.position);
    }

    case actions.RESIZE_PANEL_START:
      return ReduxUtils.replace(state, 'isResizing', true);

    case actions.RESIZE_PANEL_END:
      return ReduxUtils.replace(state, 'isResizing', false);

    case actions.UPDATE_DRAFT_MESSAGE:
      // Handled by saga - saga will dispatch SET_DRAFT_MESSAGE or SET_NEW_CHAT_DRAFT
      return state;

    case actions.SET_DRAFT_MESSAGE: {
      // Internal action dispatched by saga for existing chat drafts
      // Drafts are scoped to userId to prevent leaking across accounts
      if (!action.chatId || !action.userId) return state;
      if (!action.draftText || action.draftText.trim() === '') {
        // Empty draft - delete the key
        newState = {...state};
        newState.draftMessages = {...state.draftMessages};
        newState.draftMessages[action.userId] = {...(newState.draftMessages[action.userId] || {})};
        delete newState.draftMessages[action.userId][action.chatId];
        return newState;
      }
      return ReduxUtils.replace(state, `draftMessages.${action.userId}.${action.chatId}`, action.draftText);
    }

    case actions.SET_NEW_CHAT_DRAFT: {
      // Internal action dispatched by saga for new chat drafts
      // Drafts are scoped to userId to prevent leaking across accounts
      const key = buildContextKey(action.contextType, action.boardId, action.cardId);
      if (!key || !action.userId) return state;
      if (!action.draftText || action.draftText.trim() === '') {
        // Empty draft - delete the key
        newState = {...state};
        newState.newChatDrafts = {...state.newChatDrafts};
        newState.newChatDrafts[action.userId] = {...(newState.newChatDrafts[action.userId] || {})};
        delete newState.newChatDrafts[action.userId][key];
        return newState;
      }
      return ReduxUtils.replace(state, `newChatDrafts.${action.userId}.${key}`, action.draftText);
    }

    case actions.CLEAR_DRAFT_MESSAGE: {
      // Remove draft for existing chat
      if (!action.chatId || !action.userId) return state;
      newState = {...state};
      newState.draftMessages = {...state.draftMessages};
      newState.draftMessages[action.userId] = {...(newState.draftMessages[action.userId] || {})};
      delete newState.draftMessages[action.userId][action.chatId];
      return newState;
    }

    case actions.CLEAR_NEW_CHAT_DRAFT: {
      // Remove draft for new chat
      const key = buildContextKey(action.contextType, action.boardId, action.cardId);
      if (!key || !action.userId) return state;
      newState = {...state};
      newState.newChatDrafts = {...state.newChatDrafts};
      newState.newChatDrafts[action.userId] = {...(newState.newChatDrafts[action.userId] || {})};
      delete newState.newChatDrafts[action.userId][key];
      return newState;
    }

    case actions.SET_NEW_CHAT_ID: {
      if (!action.chatId) return state;
      const key = buildContextKey(action.contextType, action.boardId, action.cardId);
      if (!key) return state;
      return ReduxUtils.replace(state, `newChatIds.${key}`, action.chatId);
    }

    case actions.CLEAR_NEW_CHAT_ID: {
      const key = buildContextKey(action.contextType, action.boardId, action.cardId);
      if (!key) return state;
      newState = {...state};
      newState.newChatIds = {...state.newChatIds};
      delete newState.newChatIds[key];
      return newState;
    }

    case actions.MARK_BOARD_CREATION_DONE: {
      if (!action.key) return state;
      return ReduxUtils.replace(state, `completedBoardCreations.${action.key}`, true);
    }

    case actions.CANCEL_MESSAGE: {
      if (!action.messageId) return state;
      return ReduxUtils.replace(state, `cancelMessages.${action.messageId}`, true);
    }

    case actions.REGENERATE_RESPONSE: {
      if (!action.messageId) return state;
      return ReduxUtils.replace(state, `cancelMessages.${action.messageId}`, undefined);
    }

    case actions.APPROVE_TOOL_CALLS:
    case actions.REJECT_TOOL_CALLS: {
      const outcome = action.type === actions.APPROVE_TOOL_CALLS ? 'APPROVED' : 'REJECTED';
      let newState = state;
      for (const toolCallId of action.toolCallIds || []) {
        newState = ReduxUtils.replace(newState, `resolvedToolCalls.${toolCallId}`, outcome);
      }
      return newState;
    }

    // Sent with a correction: treated as approved now, as Apply is, though it is approved once the correction lands.
    case actions.UPDATE_TOOL_CALL_INPUT: {
      if (!action.shouldApprove || !action.toolCallId) return state;
      return ReduxUtils.replace(state, `resolvedToolCalls.${action.toolCallId}`, 'APPROVED');
    }

    case actions.TOOL_CALL_INPUT_FAILED: {
      if (!action.toolCallId) return state;
      return ReduxUtils.replace(state, `resolvedToolCalls.${action.toolCallId}`, undefined);
    }

    case actions.UNDO_REJECT_TOOL_CALLS: {
      let newState = state;
      for (const toolCallId of action.toolCallIds || []) {
        newState = ReduxUtils.replace(newState, `resolvedToolCalls.${toolCallId}`, undefined);
      }
      return newState;
    }

    // TODO: Revert once server fix is deployed: https://app.kerika.net/acc_4AkDDShTrU2He25uThV8UR/board/brd_1uVMIWfTIpuU9hQC3VkuOF/crd_35J3z5GgPkFpJFOCiTq9Mf?tab=description
    // case actions.CANCEL_MESSAGE_FAILED: {
    //   if (!action.messageId) return state;
    //   return ReduxUtils.replace(state, `cancelMessages.${action.messageId}`, undefined);
    // }


    case actions.SET_CHAT_ACCESS_DENIED: {
      if (!action.chatId) return state;
      return ReduxUtils.replace(state, `accessDeniedChats.${action.chatId}`, true);
    }

    case actions.SET_EDIT_MESSAGE: {
      // Set or clear editing message for a specific chat
      if (!action.chatId) return state;

      if (action.messageId === null || action.messageId === undefined) {
        // Clear editing state for this chat — and the in-progress edit text, since edit mode
        // has ended (message sent, or edit cancelled).
        newState = {...state};
        newState.editMessages = {...state.editMessages};
        delete newState.editMessages[action.chatId];
        newState.editDrafts = {...state.editDrafts};
        delete newState.editDrafts[action.chatId];
        return newState;
      }

      // Set editing message for this chat
      return ReduxUtils.replace(state, `editMessages.${action.chatId}`, action.messageId);
    }

    case actions.SET_EDIT_DRAFT: {
      // In-progress edit text for the currently-edited message. Empty clears the key.
      if (!action.chatId) return state;
      if (!action.draftText || action.draftText.trim() === '') {
        newState = {...state};
        newState.editDrafts = {...state.editDrafts};
        delete newState.editDrafts[action.chatId];
        return newState;
      }
      return ReduxUtils.replace(state, `editDrafts.${action.chatId}`, action.draftText);
    }

    case actions.SET_AUTO_SUBMIT: {
      return ReduxUtils.replace(state, `autoSubmitPending.${action.contextKey}`, true);
    }

    case actions.CLEAR_AUTO_SUBMIT: {
      const newState = { ...state, autoSubmitPending: { ...state.autoSubmitPending } };
      delete newState.autoSubmitPending[action.contextKey];
      return newState;
    }

    case actions.UPDATE_HOVER_CARD: {
      // Skip the dispatch if the value didn't actually change — avoids a
      // pointless re-render of every <kerika-board-card> on every mousemove
      // (mouseenter/mouseleave fire often as the user drifts across rows).
      if ((state.hoverCardId || null) === (action.cardId || null)) return state;
      return { ...state, hoverCardId: action.cardId || null };
    }

    case actions.REMOVE_DRAFT_CHIPS_BY_IDS: {
      const idSet = new Set(action.attachmentIds || []);
      if (idSet.size === 0) return state;

      const cleanScopedDrafts = (scopedMap) => {
        const out = {};
        for (const userId of Object.keys(scopedMap || {})) {
          const inner = scopedMap[userId] || {};
          const cleanedInner = {};
          for (const key of Object.keys(inner)) {
            const cleaned = stripChipsByIds(inner[key], idSet);
            if (cleaned && cleaned.trim() !== '') {
              cleanedInner[key] = cleaned;
            }
          }
          if (Object.keys(cleanedInner).length > 0) {
            out[userId] = cleanedInner;
          }
        }
        return out;
      };

      return {
        ...state,
        draftMessages: cleanScopedDrafts(state.draftMessages),
        newChatDrafts: cleanScopedDrafts(state.newChatDrafts),
      };
    }

    default:
      return state;
  }
};
